import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { toast } from "sonner";
import { ClipboardList, Plus, Save, Edit3, Trash2, ChevronDown, Inbox, X } from "lucide-react";

const EMPTY_Q = { label: "", type: "text", options: "" };

const STATUS_MAP = {
  pending: { label: "Da valutare", color: "#EAB308", bg: "rgba(234,179,8,0.12)" },
  accepted: { label: "Accettato", color: "#10B981", bg: "rgba(16,185,129,0.12)" },
  rejected: { label: "Rifiutato", color: "#EF4444", bg: "rgba(239,68,68,0.12)" },
};

export default function FormStudenti() {
  const [tab, setTab] = useState("risposte");
  const [questions, setQuestions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_Q);
  const [expanded, setExpanded] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchAll = () => {
    axios.get(`${API}/student-form/questions`).then(r => setQuestions(r.data)).catch(() => {});
    axios.get(`${API}/student-form/submissions`).then(r => setSubmissions(r.data)).catch(() => {});
  };
  useEffect(fetchAll, []);

  const saveQuestion = async () => {
    if (!form.label.trim()) { toast.error("Inserisci il testo della domanda"); return; }
    const payload = {
      label: form.label.trim(),
      type: form.type,
      options: form.type === "choice" ? form.options.split("\n").map(o => o.trim()).filter(Boolean) : [],
      order: editId === "new" ? questions.length : (questions.find(q => q.id === editId)?.order ?? 0)
    };
    if (form.type === "choice" && payload.options.length < 2) { toast.error("Inserisci almeno 2 opzioni (una per riga)"); return; }
    try {
      if (editId === "new") await axios.post(`${API}/student-form/questions`, payload);
      else await axios.put(`${API}/student-form/questions/${editId}`, payload);
      toast.success("Domanda salvata");
      setEditId(null); setForm(EMPTY_Q); fetchAll();
    } catch { toast.error("Errore"); }
  };

  const deleteQuestion = async (id) => {
    try { await axios.delete(`${API}/student-form/questions/${id}`); toast.success("Domanda eliminata"); fetchAll(); }
    catch { toast.error("Errore"); }
  };

  const deleteSubmission = async (id) => {
    try { await axios.delete(`${API}/student-form/submissions/${id}`); toast.success("Candidatura eliminata"); fetchAll(); }
    catch { toast.error("Errore"); }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/student-form/submissions/${id}/status`, { status });
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status } : s));
      toast.success(`Stato: ${STATUS_MAP[status].label}`);
    } catch { toast.error("Errore"); }
  };

  const visibleSubmissions = statusFilter === "all"
    ? submissions
    : submissions.filter(s => (s.status || "pending") === statusFilter);

  const tabBtn = (key, label, count) => (
    <button onClick={() => setTab(key)} className="flex-1 py-3 text-sm font-bold uppercase transition-all"
      style={{ fontFamily: "'Space Grotesk', sans-serif", backgroundColor: tab === key ? "var(--mm-gold)" : "transparent", color: tab === key ? "#0A0A0A" : "var(--mm-text-secondary)" }}
      data-testid={`tab-${key}`}>
      {label} ({count})
    </button>
  );

  return (
    <div className="page-container" data-testid="form-studenti-page">
      <PageHero image={HERO_IMAGES.dashboard} kicker="Admin" title="Form Studenti" subtitle="Gestisci le domande del form e visiona le candidature ricevute" />
      <SectionBg type="charts" />
      <div className="container py-8 relative max-w-4xl">
        <div className="flex overflow-hidden mb-8" style={{ border: "1px solid var(--mm-border)" }}>
          {tabBtn("risposte", "Candidature", submissions.length)}
          {tabBtn("domande", "Domande del form", questions.length)}
        </div>

        {tab === "domande" && (
          <div className="space-y-4">
            {editId === null && (
              <button onClick={() => { setEditId("new"); setForm(EMPTY_Q); }} className="btn-primary flex items-center gap-2 mx-auto text-sm" data-testid="add-question-btn">
                <Plus size={16} /> Aggiungi Domanda
              </button>
            )}
            {editId !== null && (
              <div className="glass-card p-5 space-y-3" data-testid="question-form">
                <input type="text" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} className="admin-input w-full" placeholder="Testo della domanda (es. Nome e Cognome)" data-testid="question-label-input" />
                <div className="flex gap-2">
                  <button onClick={() => setForm({ ...form, type: "text" })} className="flex-1 py-2 text-sm rounded-lg" style={{ border: form.type === "text" ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)", color: form.type === "text" ? "var(--mm-gold)" : "var(--mm-text-secondary)" }} data-testid="type-text-btn">Risposta libera</button>
                  <button onClick={() => setForm({ ...form, type: "choice" })} className="flex-1 py-2 text-sm rounded-lg" style={{ border: form.type === "choice" ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)", color: form.type === "choice" ? "var(--mm-gold)" : "var(--mm-text-secondary)" }} data-testid="type-choice-btn">Scelta multipla</button>
                </div>
                {form.type === "choice" && (
                  <textarea value={form.options} onChange={e => setForm({ ...form, options: e.target.value })} className="admin-textarea w-full" rows={4} placeholder={"Un'opzione per riga, es:\nSì\nNo\nForse"} data-testid="question-options-input" />
                )}
                <div className="flex gap-3">
                  <button onClick={saveQuestion} className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-2" data-testid="question-save-btn"><Save size={14} /> Salva</button>
                  <button onClick={() => { setEditId(null); setForm(EMPTY_Q); }} className="btn-secondary flex-1 py-2 text-sm flex items-center justify-center gap-2"><X size={14} /> Annulla</button>
                </div>
              </div>
            )}
            {questions.map((q, i) => (
              <div key={q.id} className="glass-card p-4 flex items-center gap-4" data-testid={`question-row-${i}`}>
                <ClipboardList size={18} style={{ color: "var(--mm-gold)", flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate" style={{ color: "var(--mm-text-primary)" }}>{q.label}</p>
                  <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>
                    {q.type === "choice" ? `Scelta multipla: ${(q.options || []).join(" / ")}` : "Risposta libera"}
                  </p>
                </div>
                <button onClick={() => { setEditId(q.id); setForm({ label: q.label, type: q.type, options: (q.options || []).join("\n") }); }} className="p-2 rounded-lg" style={{ backgroundColor: "rgba(47,216,168,0.12)" }} data-testid={`edit-question-${i}`}>
                  <Edit3 size={15} style={{ color: "var(--mm-gold)" }} />
                </button>
                <button onClick={() => deleteQuestion(q.id)} className="p-2 rounded-lg" style={{ backgroundColor: "rgba(239,68,68,0.12)" }} data-testid={`delete-question-${i}`}>
                  <Trash2 size={15} style={{ color: "#EF4444" }} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "risposte" && (
          <div className="space-y-4">
            {/* Filtro stato */}
            <div className="flex flex-wrap gap-2">
              {[["all", `Tutte (${submissions.length})`], ...Object.entries(STATUS_MAP).map(([k, v]) => [k, `${v.label} (${submissions.filter(s => (s.status || "pending") === k).length})`])].map(([key, label]) => (
                <button key={key} onClick={() => setStatusFilter(key)}
                  className="px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all"
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    backgroundColor: statusFilter === key ? (STATUS_MAP[key]?.bg || "rgba(47,216,168,0.15)") : "transparent",
                    border: statusFilter === key ? `1px solid ${STATUS_MAP[key]?.color || "var(--mm-gold)"}` : "1px solid var(--mm-border)",
                    color: statusFilter === key ? (STATUS_MAP[key]?.color || "var(--mm-gold)") : "var(--mm-text-muted)"
                  }}
                  data-testid={`status-filter-${key}`}>
                  {label}
                </button>
              ))}
            </div>

            {visibleSubmissions.length === 0 && (
              <div className="glass-card p-10 text-center">
                <Inbox size={40} className="mx-auto mb-3" style={{ color: "var(--mm-gold)" }} />
                <p style={{ color: "var(--mm-text-secondary)" }}>{submissions.length === 0 ? "Nessuna candidatura ricevuta finora" : "Nessuna candidatura con questo stato"}</p>
              </div>
            )}
            {visibleSubmissions.map((s, i) => {
              const isOpen = expanded === s.id;
              const first = s.answers?.[0]?.answer || "Candidatura";
              const st = STATUS_MAP[s.status || "pending"];
              return (
                <div key={s.id} className="glass-card overflow-hidden" data-testid={`submission-row-${i}`}>
                  <div role="button" tabIndex={0} onClick={() => setExpanded(isOpen ? null : s.id)} onKeyDown={e => e.key === "Enter" && setExpanded(isOpen ? null : s.id)}
                    className="w-full p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate" style={{ color: "var(--mm-text-primary)", fontFamily: "'Space Grotesk', sans-serif" }} data-testid={`submission-name-${i}`}>{first}</p>
                      <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>
                        {s.created_at ? new Date(s.created_at).toLocaleString("it-IT") : ""}
                      </p>
                    </div>
                    <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded flex-shrink-0" style={{ backgroundColor: st.bg, color: st.color, border: `1px solid ${st.color}50`, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.06em" }} data-testid={`submission-status-${i}`}>
                      {st.label}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); deleteSubmission(s.id); }} className="p-2 rounded-lg" style={{ backgroundColor: "rgba(239,68,68,0.12)" }} data-testid={`delete-submission-${i}`}>
                      <Trash2 size={15} style={{ color: "#EF4444" }} />
                    </button>
                    <ChevronDown size={18} style={{ color: "var(--mm-gold)", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                  </div>
                  {isOpen && (
                    <div className="px-5 pb-5 pt-1 space-y-3" style={{ borderTop: "1px solid var(--mm-border)" }} data-testid={`submission-detail-${i}`}>
                      <div className="flex flex-wrap gap-2 pt-3">
                        {Object.entries(STATUS_MAP).map(([key, v]) => {
                          const active = (s.status || "pending") === key;
                          return (
                            <button key={key} onClick={() => setStatus(s.id, key)}
                              className="px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all"
                              style={{
                                fontFamily: "'Space Grotesk', sans-serif",
                                backgroundColor: active ? v.bg : "transparent",
                                border: active ? `1px solid ${v.color}` : "1px solid var(--mm-border)",
                                color: active ? v.color : "var(--mm-text-muted)"
                              }}
                              data-testid={`set-status-${key}-${i}`}>
                              {v.label}
                            </button>
                          );
                        })}
                      </div>
                      {(s.answers || []).map((a, ai) => (
                        <div key={ai} className="pt-3">
                          <p className="text-xs font-bold uppercase mb-1" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.08em" }}>{a.question}</p>
                          <p className="text-sm whitespace-pre-line" style={{ color: "var(--mm-text-primary)" }}>{a.answer}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
